import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatMiniFabButton } from '@angular/material/button';
import {
  MatSlideToggle,
  MatSlideToggleChange,
} from '@angular/material/slide-toggle';
import { MatIcon } from '@angular/material/icon';
import { MatOption } from '@angular/material/core';
import {
  MatFormField,
  MatSelect,
  MatSelectChange,
} from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyFilter } from '../../../shared/model/company.model';
import { CompanyService } from '../../../services/company.service';
import {
  PROFILER_LANGUAGE_LABEL_KEYS,
  ProfilerLanguage,
} from '../../../shared/constants/profiler-language';
import { ProfilerLanguageFlagComponent } from '../../../shared/components/profiler-language-flag/profiler-language-flag.component';
import { CompletenessLevel } from '../../../shared/model/profile-completeness';
import {
  CompletenessCounts,
  CompletenessFilterComponent,
  emptyCompletenessCounts,
} from '../completeness-filter/completeness-filter.component';
import { ConsultantSearchFieldComponent } from '../search-field/consultant-search-field.component';
import { SearchScope, toSearchScope, toSearchTerm } from '../search-scope';

@Component({
  selector: 'app-sorter',
  imports: [
    MatSlideToggle,
    MatIcon,
    MatSelect,
    MatOption,
    MatFormField,
    TranslatePipe,
    MatMiniFabButton,
    MatTooltip,
    NgClass,
    ProfilerLanguageFlagComponent,
    CompletenessFilterComponent,
    ConsultantSearchFieldComponent,
  ],
  templateUrl: './sorter.component.html',
  styleUrl: './sorter.component.scss',
  standalone: true,
})
export class SorterComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('sorterWrapper', { static: false })
  sorterWrapper!: ElementRef<HTMLDivElement>;

  @ViewChild('sortSelect', { static: false }) sortSelect!: MatSelect;

  private sorterResize?: ResizeObserver;

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private route: ActivatedRoute,
    private companyService: CompanyService,
  ) {}

  get companies() {
    return this.companyService.companies;
  }

  @Input() amount: number;
  @Input() sortValue: string;
  @Input() selectedCompany: CompanyFilter = 'ALL';
  @Input() applicantOnly: boolean = false;
  @Input() freelancerOnly: boolean = false;
  @Input() profilerLanguage: ProfilerLanguage | null = null;
  @Input() searchScope: SearchScope = 'name';
  // NP20-1192: Die Ampelleiste gehoert in den fixierten Wrapper der Werkzeugleiste —
  // ausserhalb liefe sie unter ihr durch. Die Werte reicht die Uebersicht durch, wie
  // bei Firmen-, Bewerber- und Freelancer-Filter.
  @Input() completenessLevels: CompletenessLevel[] = [];
  @Input() completenessCounts: CompletenessCounts = emptyCompletenessCounts();
  @Output() opened = new EventEmitter<boolean>();
  @Output() sortBy = new EventEmitter<string>();
  @Output() isArchiving = new EventEmitter<boolean>();
  @Output() isListView = new EventEmitter<boolean>();
  @Output() searchValueChanged = new EventEmitter<string>();
  @Output() searchScopeChanged = new EventEmitter<SearchScope>();
  @Output() companyFilterChange = new EventEmitter<CompanyFilter>();
  @Output() applicantFilterChange = new EventEmitter<boolean>();
  @Output() freelancerFilterChange = new EventEmitter<boolean>();
  @Output() completenessLevelsChange = new EventEmitter<CompletenessLevel[]>();

  searchValueString: string = '';

  get profilerLanguageLabelKey(): string {
    return this.profilerLanguage
      ? PROFILER_LANGUAGE_LABEL_KEYS[this.profilerLanguage]
      : '';
  }

  searchTimeout?: ReturnType<typeof setTimeout>;
  showSelect: boolean = false;

  ngOnInit() {
    this.companyService.load().subscribe();
    this.route.queryParams.subscribe((params) => {
      if (params['in']) {
        this.searchScope = toSearchScope(params['in']);
      }
      // `search` ist der alte Parameter und wird weiter gelesen.
      const term = toSearchTerm(params['q'] ?? params['search']);
      if (term) {
        this.searchValueString = term;
        this.searchValueChanged.emit(this.searchValueString);
      }
    });
  }

  ngOnDestroy() {
    clearTimeout(this.searchTimeout);
    this.sorterResize?.disconnect();
  }

  chipStyle(color: string, active: boolean): Record<string, string> {
    if (!active) {
      return {};
    }
    const safe = (color || '').trim();
    if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(safe)) {
      return {};
    }
    return {
      background: `color-mix(in srgb, ${safe} 12%, white)`,
      color: safe,
      'border-color': `color-mix(in srgb, ${safe} 45%, white)`,
    };
  }

  ngAfterViewInit(): void {
    this.updateSorterHeight();
    this.observeSorterHeight();
    if (this.sortValue) {
      this.showSelect = true;
    }
  }

  // Die Leiste bricht auf schmalen Fenstern um und wird dabei hoeher; ohne das
  // Nachmessen verdeckt sie die erste Kachelreihe.
  private observeSorterHeight(): void {
    if (!this.sorterWrapper || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.sorterResize = new ResizeObserver(() => this.updateSorterHeight());
      this.sorterResize.observe(this.sorterWrapper.nativeElement);
    });
  }

  private updateSorterHeight(): void {
    if (!this.sorterWrapper) {
      return;
    }

    const sorterHeight = this.sorterWrapper.nativeElement.offsetHeight;
    const navOffset = 60;
    const extraSpace = 16;

    const total = sorterHeight + navOffset + extraSpace;

    this.ngZone.runOutsideAngular(() => {
      document.documentElement.style.setProperty(
        '--sorter-height',
        `${total}px`,
      );
    });
  }

  onSort(event: MatSelectChange<any>) {
    this.sortBy.emit(event.value);
  }

  toggleSelect() {
    if (this.showSelect) {
      this.sortSelect.close();
      this.showSelect = false;

      this.sortValue = '';
      this.sortBy.emit('');

      setTimeout(() => this.updateSorterHeight(), 0);
      return;
    }
    this.showSelect = true;

    setTimeout(() => {
      if (this.sortSelect) {
        this.sortSelect.open();
      }
      this.updateSorterHeight();
    }, 0);
  }

  onArchive(event: MatSlideToggleChange) {
    this.isArchiving.emit(event.checked);
  }

  onListView(event: MatSlideToggleChange) {
    this.isListView.emit(event.checked);
  }

  onFilter() {
    this.opened.emit(true);
  }

  onCompanyFilter(value: CompanyFilter) {
    if (this.selectedCompany === value) {
      return;
    }
    this.companyFilterChange.emit(value);
  }

  onApplicantFilter() {
    this.applicantFilterChange.emit(!this.applicantOnly);
  }

  onFreelancerFilter() {
    this.freelancerFilterChange.emit(!this.freelancerOnly);
  }

  /** Jede Eingabe kommt roh aus dem Suchfeld; die Wartezeit bleibt hier. */
  onSearchTermInput(value: string) {
    this.searchValueString = value;
    this.searchForEntry();
  }

  /** Der Bereich wirkt sofort — ohne Wartezeit, der Suchbegriff bleibt stehen. */
  onSearchScopeChange(scope: SearchScope) {
    this.searchScope = scope;
    this.searchScopeChanged.emit(scope);
  }

  searchForEntry() {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      if (this.isNavigatingAway()) {
        return;
      }

      this.searchValueChanged.emit(this.searchValueString);

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          in: this.searchScope,
          q: this.searchValueString || null,
          search: null,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }, 300);
  }

  private isNavigatingAway(): boolean {
    const navigation = this.router.getCurrentNavigation();
    if (!navigation) {
      return false;
    }

    const target = navigation.finalUrl ?? navigation.extractedUrl;
    return (
      this.pathOf(this.router.serializeUrl(target)) !==
      this.pathOf(this.router.url)
    );
  }

  private pathOf(url: string): string {
    return url.split(/[?#]/)[0];
  }
}
